/**
 * Text Globe Component
 *
 * - Splits [data-splittext] text into letter spans with --i (0..1) positioning
 *   around a 360° Y-axis orbit.
 * - Words become groups: each word anchors at an evenly-spaced orbit position;
 *   letters within a word use a step scaled to word length.
 * - Rotation is JS-driven (RAF loop) so click-drag interacts with it:
 *     • Drag to spin manually
 *     • Release → momentum decays back to auto-spin speed
 */

(function (): void {
    const BASE_STEP  = 0.065; // step for a 3-letter word (fraction of 360°)
    const STEP_SCALE = 0.035; // extra step per letter beyond 3

    const ORBIT_TILT = -5;   // deg — X-axis tilt of the letter ring (negative tilts top away)

    const AUTO_VEL   = -0.12; // deg/frame auto-spin
    const FRICTION   = 0.92;  // momentum decay per frame after release
    const SENSITIVITY = 0.55; // drag px → degrees

    // ── Letter splitting ────────────────────────────────────────────────────

    function splitText(el: HTMLElement): void {
        const text  = (el.textContent ?? '').trim();
        const words = text.split(/\s+/).filter(Boolean);

        if (words.length < 2) {
            const letters = text.split('');
            const total   = letters.length + 1;
            el.innerHTML  = letters
                .map((c, i) => `<span style="--i:${i / total}">${c}</span>`)
                .join('');
            return;
        }

        const html: string[] = [];
        words.forEach(function (word, wIdx) {
            const anchor  = wIdx / words.length;
            const letters = word.split('');
            const step    = BASE_STEP + (letters.length - 3) * STEP_SCALE;
            letters.forEach(function (c, lIdx) {
                html.push(`<span style="--i:${anchor + lIdx * step}">${c}</span>`);
            });
        });
        el.innerHTML = html.join('');
    }

    // ── JS-driven rotation with drag ────────────────────────────────────────

    function initRotation(h1: HTMLElement, dragArea: HTMLElement): void {
        let angle    = 0;
        let velocity = AUTO_VEL;

        let isDragging  = false;
        let lastX       = 0;
        let dragVelocity = 0;

        // Cursor feedback
        dragArea.style.cursor = 'grab';

        function onPointerDown(e: PointerEvent): void {
            isDragging   = true;
            lastX        = e.clientX;
            dragVelocity = 0;
            dragArea.style.cursor = 'grabbing';
            dragArea.setPointerCapture(e.pointerId);
            e.preventDefault();
        }

        function onPointerMove(e: PointerEvent): void {
            if (!isDragging) return;
            const delta  = (e.clientX - lastX) * SENSITIVITY;
            angle       += delta;
            dragVelocity = delta;
            lastX        = e.clientX;
        }

        function onPointerUp(e: PointerEvent): void {
            if (!isDragging) return;
            isDragging            = false;
            velocity              = dragVelocity; // carry momentum
            dragArea.style.cursor = 'grab';
            dragArea.releasePointerCapture(e.pointerId);
        }

        dragArea.addEventListener('pointerdown', onPointerDown);
        dragArea.addEventListener('pointermove', onPointerMove);
        dragArea.addEventListener('pointerup',   onPointerUp);
        dragArea.addEventListener('pointercancel', onPointerUp);

        // RAF loop — h1 transform is set here; no CSS animation needed
        function tick(): void {
            requestAnimationFrame(tick);

            if (!isDragging) {
                // Blend velocity toward auto-spin with friction
                velocity = velocity * FRICTION + AUTO_VEL * (1 - FRICTION);
                angle   += velocity;
            }

            h1.style.transform = `rotateX(${ORBIT_TILT}deg) rotateY(${angle}deg)`;
        }

        requestAnimationFrame(tick);

        // Reveal after globe is ready + damping has settled (~30 frames)
        document.addEventListener('globe:ready', function () {
            let frames = 0;
            function waitForSettle(): void {
                if (++frames < 30) { requestAnimationFrame(waitForSettle); return; }
                h1.classList.add('is-ready');
            }
            requestAnimationFrame(waitForSettle);
        }, { once: true });
    }

    // ── Init ────────────────────────────────────────────────────────────────

    function init(): void {
        document.querySelectorAll<HTMLElement>('[data-splittext]').forEach(function (el) {
            splitText(el);

            // Use the immediate parent (.tg-scene) as the drag area — keeps hero-copy interactive
            const dragArea =
                (el.parentElement as HTMLElement) ??
                el;

            initRotation(el, dragArea);
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
